import React, { useCallback, useState } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import ButtonV2 from '../../button/buttonV2';
import { GroupBody, GroupModal, GroupModalHead, GroupModel, Icon, InputValues, ModelTitle, TodoText } from './style';
import { TaskGroupInterface } from '../../../interfaces/TaskInterface';
import { isEmpty } from '../../../helpers/common';
import { createNewGroup } from '../../../services/taskServices';
import { useDispatch } from 'react-redux';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { updateGroupDetail } from '../../../services/taskServices';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { rootStore } from '../../../mobx/rootStore';
import { captureException } from '../../../services/logService';
import { nanoid } from 'nanoid';
import { BUTTON_TYPE } from '../../../global/constants';

interface Props {
  groupData?: TaskGroupInterface | null;
  loadData?: () => void;
  onClose?: () => void;
  isTemplateProject?: boolean;
}

const CreateGroupModal: React.FC<Props> = (props) => {
  //Props declaration
  const { groupData, loadData, onClose, isTemplateProject } = props;

  //States
  const [groupName, setGroupName] = useState<string>(groupData?.name || '');
  const [loading, setLoading] = useState<boolean>(false);

  //Mobx store
  const { projectStore, projectTemplateStore } = rootStore || {};
  const { currentProject } = projectStore || {};
  const { currentProjectTemplate } = projectTemplateStore || {};

  //Other variable
  const dispatch = useDispatch();

  //Handle group name change
  const onChangeInput = useCallback((value: string) => {
    setGroupName(value);
  }, []);

  //Handle create or edit group button
  const onCreateGroupButton = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        let response;
        if (!isEmpty(groupData?.id)) {
          if (isTemplateProject) {
            const updatedGroups =
              currentProjectTemplate?.taskGroups?.map((item) => {
                if (item?.id === groupData?.id) {
                  return {
                    ...item,
                    name: groupName
                  };
                }
                return item;
              }) || [];
            currentProjectTemplate?.setTaskGroups(updatedGroups);
          } else {
            const payload = { userId: userDetails?.id, itemId: groupData?.id, name: groupName };
            response = await dispatch(updateGroupDetail(payload));
          }
        } else {
          if (isTemplateProject) {
            const updatedGroups = [
              ...(currentProjectTemplate?.taskGroups || []),
              {
                id: String(nanoid()),
                name: groupName,
                projectId: currentProjectTemplate?.id || '',
                index: currentProjectTemplate?.taskGroups?.length || 0,
                isCollapse: false
              }
            ];
            currentProjectTemplate?.setTaskGroups(updatedGroups);
          } else {
            const payload = { userId: userDetails?.id, name: groupName, projectId: currentProject?.id };
            response = await dispatch(createNewGroup(payload));
          }
        }
        if (response) {
          trackAnalyticActivity(PROJECT_ANALYTICS.GROUP_ADDED);
          loadData?.();
        }
      } catch (e) {
        captureException(e);
        console.log('ERROR', e);
      } finally {
        onClose?.();
        setGroupName('');
        setLoading(false);
      }
    },
    [
      currentProject?.id,
      currentProjectTemplate,
      dispatch,
      groupData?.id,
      groupName,
      isTemplateProject,
      loadData,
      onClose
    ]
  );

  return (
    <GroupModel onSubmit={onCreateGroupButton}>
      <GroupModalHead>
        <TodoText>{groupData?.id ? 'Update Group' : 'Create Group'} </TodoText>
        <Icon onClick={onClose}>
          <SVGIcon name='close-icon' width='18' height='18' viewBox='0 0 18 18' className='closeicon' />
        </Icon>
      </GroupModalHead>
      <GroupBody>
        <ModelTitle>Group name</ModelTitle>
        <InputValues
          autoFocus={true}
          placeholder='Enter your group name'
          value={groupName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeInput(e.target.value)}
        />
      </GroupBody>
      <GroupModal>
        <ButtonV2
          variant={BUTTON_TYPE.PRIMARY}
          buttonType={'submit'}
          title={groupData?.id ? 'Update Group' : 'Create Group'}
          isLoading={loading}
        />
      </GroupModal>
    </GroupModel>
  );
};

export default CreateGroupModal;
